// src/rms/pages/Billing.js
// ============================================================================
// Billing
// ============================================================================
// One list for both billing shapes. Per-rental invoices carry an agreement;
// recurring storage invoices carry a subscription and a billing period. The
// recurring biller can be run from here so the monthly cycle is visible rather
// than magic.
//
// The invoice list is a browsing screen and carries a sidebar ad; the payment
// modal is transactional and does not.
// ============================================================================

import React, { useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import { Link } from "react-router-dom";
import { useCollection, indexById } from "../hooks/useCollection";
import rentalService from "../data/rentalService";
import { PageHeader, Stat, StatusPill, Modal, Field, Select, Banner, EmptyState, formatDate } from "../components/ui";
import { formatRatePercent } from "../pricing/taxEngine";
import adEngine from "../ads/adEngine";
import pinService from "../data/pinService";
import PinModal from "../components/PinModal";

function Billing() {
  const { operator, t, tLower, formatMoney, isContinuous, invalidate } = useRms();
  const { rows: invoices } = useCollection("invoices", {}, { orderBy: "issued_at", direction: "desc" });
  const { rows: customers } = useCollection("customers");
  const { rows: subscriptions } = useCollection("subscriptions");
  const { rows: taxRates } = useCollection("taxRates");

  const [paying, setPaying] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [notice, setNotice] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const customersById = useMemo(() => indexById(customers), [customers]);

  const outstanding = invoices
    .filter((i) => ["sent", "partially_paid", "overdue"].includes(i.status))
    .reduce((s, i) => s + (i.total_cents - i.amount_paid_cents), 0);

  const collected = invoices.reduce((s, i) => s + i.amount_paid_cents, 0);

  const mrr = subscriptions
    .filter((s) => ["active", "past_due"].includes(s.status))
    .reduce((s, sub) => s + sub.amount_cents, 0);

  const dueSubs = subscriptions.filter(
    (s) => ["active", "past_due"].includes(s.status) && s.next_bill_date <= new Date().toISOString().slice(0, 10)
  );

  const filtered = statusFilter ? invoices.filter((i) => i.status === statusFilter) : invoices;

  const handleRunBilling = async () => {
    adEngine.recordAction();
    const result = await rentalService.runRecurringBilling(operator);
    const count = result.data?.length || 0;
    setNotice(
      count
        ? t("billing.raised", "{count, plural, one {Raised # recurring invoice.} other {Raised # recurring invoices.}}", { count })
        : "Nothing was due — no invoices raised."
    );
    invalidate();
  };

  /**
   * The payment, waiting on a signature.
   *
   * Yes, this is a second PIN after the one that opened Billing -- and they
   * record different things. The first says who came in; this one is bound to
   * THIS payment row by a trigger and cannot be moved to another. Money is the
   * thing most worth being able to attribute, so the extra four digits buy
   * something real.
   */
  const [pendingPayment, setPendingPayment] = useState(null);

  const handlePay = async (invoice, amountCents, method) => {
    adEngine.recordAction();
    setPendingPayment({ invoice, amountCents, method });
  };

  const signAndPay = async (pin) => {
    const signed = await pinService.signAction(operator, {
      pin,
      action: "payment.take",
      subjectTable: "rms_payments",
      context: { amount_cents: pendingPayment.amountCents, method: pendingPayment.method },
    });
    if (!signed.success) return { error: signed.error };

    const result = await rentalService.recordPayment(operator, {
      invoiceId: pendingPayment.invoice.id,
      amountCents: pendingPayment.amountCents,
      method: pendingPayment.method,
      signatureId: signed.data,
    });
    if (!result.success) return { error: result.error };

    setPendingPayment(null);
    setPaying(null);
    invalidate();
    return {};
  };

  return (
    <>
      <PageHeader
        title={t("billing.billing", "Billing")}
        subtitle={t("billing.subtitle", "{count, plural, one {# invoice} other {# invoices}} · {amount} outstanding", {
          count: invoices.length,
          amount: formatMoney(outstanding),
        })}
        actions={
          isContinuous ? (
            <button type="button" className="rms-btn rms-btn--primary" onClick={handleRunBilling}>
              {t("billing.runMonthly", "Run monthly billing")}
              {dueSubs.length ? t("billing.due", " ({count} due)", { count: dueSubs.length }) : ""}
            </button>
          ) : null
        }
      />

      {notice ? <Banner tone="ok">{notice}</Banner> : null}

      {/* Tax is not asked for during onboarding and nothing blocks an operator
          from invoicing without it, so this is the one place a missing rate has
          to be impossible to miss: the screen where money is being recorded. */}
      {!taxRates.some((r) => r.is_active !== false) ? (
        <Banner tone="warn">
          {t(
            "billing.noTaxConfiguredBanner",
            "No tax rates are set up, so every invoice is being raised with no tax on it."
          )}{" "}
          <Link to="/rms/settings/tax">{t("billing.setUpTax", "Set up tax")}</Link>
        </Banner>
      ) : null}

      <div>
        <div>
          <div className="rms-stat-row">
            <Stat label={t("billing.outstanding", "Outstanding")} value={formatMoney(outstanding)} />
            <Stat label={t("billing.collected", "Collected")} value={formatMoney(collected)} />
            {isContinuous ? (
              <Stat label={t("billing.monthlyRecurring", "Monthly recurring")} value={formatMoney(mrr)} hint={t("billing.activeLeases", "{count, plural, one {# active lease} other {# active leases}}", {
                count: subscriptions.filter((s) => s.status === "active").length,
              })} />
            ) : (
              <Stat label={t("billing.invoices", "Invoices")} value={invoices.length} hint={t("billing.paidCount", "{count} paid", {
                count: invoices.filter((i) => i.status === "paid").length,
              })} />
            )}
          </div>

          <select className="rms-select" style={{ maxWidth: 200, marginBottom: 16 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t("billing.allInvoices", "All invoices")}</option>
            {["draft", "sent", "partially_paid", "paid", "overdue", "void"].map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, " ")}
              </option>
            ))}
          </select>

          {invoices.length === 0 ? (
            <EmptyState
              title={t("billing.noInvoicesYet", "No invoices yet")}
              message={
                isContinuous
                  ? "Invoices appear when you run monthly billing for occupied units."
                  : t("billing.empty.perRental", "Invoices are raised automatically when you {action} a rental.", {
                    action: tLower("checkin", "check in"),
                  })
              }
            />
          ) : (
            <div className="rms-table-wrap">
              <table className="rms-table">
                <thead>
                  <tr>
                    <th>{t("billing.invoice", "Invoice")}</th>
                    <th>{t("customer", "Customer")}</th>
                    <th>{t("certifications.issued", "Issued")}</th>
                    <th className="rms-num">{t("billing.total", "Total")}</th>
                    <th className="rms-num">{t("billing.balance", "Balance")}</th>
                    <th>{t("campaignForm.status", "Status")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => {
                    const balance = inv.total_cents - inv.amount_paid_cents;
                    const customer = customersById[inv.customer_id];
                    return (
                      <tr key={inv.id}>
                        <td style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12.5 }}>
                          {inv.invoice_number}
                          {inv.subscription_id ? (
                            <div style={{ fontSize: 11, color: "var(--rms-muted)", fontFamily: "inherit" }}>
                              {formatDate(inv.period_start)} – {formatDate(inv.period_end)}
                            </div>
                          ) : null}
                        </td>
                        <td>{customer ? `${customer.first_name} ${customer.last_name || ""}`.trim() : "—"}</td>
                        <td style={{ fontSize: 12.5 }}>{formatDate(inv.issued_at)}</td>
                        <td className="rms-num">{formatMoney(inv.total_cents)}</td>
                        <td className="rms-num" style={{ color: balance > 0 ? "var(--rms-danger)" : "var(--rms-muted)" }}>
                          {balance > 0 ? formatMoney(balance) : "—"}
                        </td>
                        <td>
                          <StatusPill status={inv.status} />
                        </td>
                        <td className="rms-num" style={{ whiteSpace: "nowrap" }}>
                          <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setViewing(inv)}>
                            {t("customers.view", "View")}
                          </button>{" "}
                          {balance > 0 ? (
                            <button type="button" className="rms-btn rms-btn--primary rms-btn--sm" onClick={() => setPaying(inv)}>
                              {t("billing.takePayment", "Take payment")}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {viewing ? (
        <Modal title={`Invoice ${viewing.invoice_number}`} onClose={() => setViewing(null)}>
          {/* An invoice is formatted in the currency it was RAISED in, which is
              not necessarily the one the business uses today. */}
          <table className="rms-table" style={{ minWidth: 0 }}>
            <thead>
              <tr>
                <th>{t("billing.item", "Item")}</th>
                <th className="rms-num">{t("billing.qty", "Qty")}</th>
                <th className="rms-num">{t("billing.amount", "Amount")}</th>
              </tr>
            </thead>
            <tbody>
              {viewing.line_items.map((l, i) => (
                <tr key={i}>
                  <td>
                    {l.description}
                    <div style={{ fontSize: 11.5, color: "var(--rms-muted)" }}>{l.kind.replace(/_/g, " ")}</div>
                  </td>
                  <td className="rms-num">{l.quantity}</td>
                  <td className="rms-num">{formatMoney(l.amount_cents, viewing.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--rms-border)" }}>
            <InvoiceTotals invoice={viewing} formatMoney={formatMoney} operator={operator} />
          </div>
        </Modal>
      ) : null}

      {paying ? (
        <PaymentModal invoice={paying} formatMoney={formatMoney} currency={operator.currency} onCancel={() => setPaying(null)} onPay={handlePay} />
      ) : null}

      {pendingPayment ? (
        <PinModal
          title={t("billing.signPayment", "Who is taking this payment?")}
          explain={t("billing.signPaymentHelp", "Your PIN is bound to this payment and cannot be moved to another.")}
          onCancel={() => setPendingPayment(null)}
          onResolved={signAndPay}
        />
      ) : null}
    </>
  );
}

/** Transactional — deliberately contains no ad slot. */
function PaymentModal({ invoice, formatMoney, currency, onCancel, onPay }) {
  const { t } = useRms();
  const balance = invoice.total_cents - invoice.amount_paid_cents;
  const [amount, setAmount] = useState((balance / 100).toFixed(2));
  const [method, setMethod] = useState("card");

  return (
    <Modal
      title={`Take payment — ${invoice.invoice_number}`}
      onClose={onCancel}
      width={460}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="rms-btn rms-btn--primary"
            onClick={() => onPay(invoice, Math.round(Number(amount) * 100), method)}
            disabled={!Number(amount)}
          >
            {t("billing.recordPayment", "Record payment")}
          </button>
        </>
      }
    >
      <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
        Balance outstanding: <strong>{formatMoney(balance)}</strong>
      </p>
      <Field label={`Amount (${currency})`} required>
        <input className="rms-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label={t("billing.method", "Method")}>
        <Select
          value={method}
          onChange={setMethod}
          placeholder={t("billing.card", "Card")}
          options={[
            { value: "card", label: "Card", labelKey: "opt.paymentMethod.card" },
            { value: "cash", label: "Cash", labelKey: "opt.paymentMethod.cash" },
            { value: "check", label: "Check", labelKey: "opt.paymentMethod.check" },
            { value: "bank_transfer", label: "Bank transfer", labelKey: "opt.paymentMethod.bankTransfer" },
            { value: "other", label: "Other", labelKey: "opt.paymentMethod.other" },
          ]}
        />
      </Field>
    </Modal>
  );
}

/**
 * The totals block, which is genuinely different in the two pricing modes.
 *
 * Exclusive, the customer adds tax to a subtotal:
 *     Subtotal              $100.00
 *     CA state 6%             $6.00
 *     Alameda County 1.25%    $1.25
 *     Total                 $107.25
 *
 * Inclusive, the total IS the advertised price and tax is disclosed inside it:
 *     Total                 £120.00
 *        includes VAT 20%    £20.00
 *
 * Everything is read from tax_breakdown, the snapshot taken when the invoice
 * was raised -- never from the operator's live configuration. Reprinting a
 * two-year-old invoice must show the rates that were charged then, not the
 * rates in force today.
 */
/** Indented under the tax line it belongs to, and quieter than it. */
function RegistrationNote({ text }) {
  return (
    <div style={{ fontSize: 11.5, color: "var(--rms-muted)", paddingInlineStart: 18, marginTop: -2, marginBottom: 4 }}>
      {text}
    </div>
  );
}

/** Custom legal lines, then the address block, both opt-in and often empty. */
function BusinessDetails({ operator }) {
  if (!operator) return null;

  const lines = Array.isArray(operator.invoice_legal_lines) ? operator.invoice_legal_lines : [];
  const printed = lines.filter((l) => l && (l.label || l.value));
  const showContact = !!operator.show_business_details_on_invoices;
  const contact = showContact
    ? [operator.business_phone, operator.business_email].filter(Boolean).join(" · ")
    : null;
  const address = showContact ? operator.business_address : null;

  if (!printed.length && !address && !contact) return null;

  return (
    <div style={{ marginTop: 8, fontSize: 12, color: "var(--rms-muted)" }}>
      {printed.map((l, i) => (
        <div key={i}>
          {[l.label, l.value].filter(Boolean).join(" ")}
        </div>
      ))}
      {/* pre-wrap, because the operator typed the line breaks and they are the
          only thing giving an unstructured address its shape. */}
      {address ? <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{address}</div> : null}
      {contact ? <div>{contact}</div> : null}
    </div>
  );
}

function InvoiceTotals({ invoice, formatMoney, operator }) {
  const { t } = useRms();
  const money = (cents) => formatMoney(cents, invoice.currency);
  const breakdown = invoice.tax_breakdown || {};
  const components = breakdown.components || [];
  const inclusive = breakdown.mode === "inclusive";
  const balance = invoice.total_cents - invoice.amount_paid_cents;

  const rateLabel = (c) => `${c.name} ${formatRatePercent(c.rate_ppm)}%`;

  /**
   * A tax that is collected by someone other than your main registration says
   * so under its own line, rather than at the foot of the page where the
   * business-level number sits. Quebec wants the QST number against the QST.
   *
   * Read from the snapshot, like everything else here: an invoice raised before
   * the operator ticked the box keeps the face it was issued with.
   */
  const registrationLine = (c) =>
    c.show_registration && (c.authority || c.registration_number)
      ? [c.authority, c.registration_number].filter(Boolean).join(" · ")
      : null;

  return (
    <>
      {inclusive ? (
        <>
          <Row label={t("billing.total", "Total")} value={money(invoice.total_cents)} strong />
          {components.map((c) => (
            <React.Fragment key={c.rate_id}>
              <Row label={`   includes ${rateLabel(c)}`} value={money(c.tax_cents)} />
              {registrationLine(c) ? <RegistrationNote text={registrationLine(c)} /> : null}
            </React.Fragment>
          ))}
        </>
      ) : (
        <>
          <Row label={t("billing.subtotal", "Subtotal")} value={money(invoice.subtotal_cents)} />
          {components.map((c) => (
            <React.Fragment key={c.rate_id}>
              <Row label={rateLabel(c)} value={money(c.tax_cents)} />
              {registrationLine(c) ? <RegistrationNote text={registrationLine(c)} /> : null}
            </React.Fragment>
          ))}
          {/* Older invoices predate the breakdown and only have a total. */}
          {!components.length && invoice.tax_cents ? (
            <Row label={t("billing.tax", "Tax")} value={money(invoice.tax_cents)} />
          ) : null}
          <Row label={t("billing.total", "Total")} value={money(invoice.total_cents)} strong />
        </>
      )}

      <Row label={t("billing.paid", "Paid")} value={money(invoice.amount_paid_cents)} />
      <Row label={t("billing.balance", "Balance")} value={money(balance)} strong />

      {breakdown.operator_registration ? (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--rms-muted)" }}>
          {breakdown.operator_registration.label} {breakdown.operator_registration.number}
        </div>
      ) : null}

      {/* Everything else the business is required, or chooses, to show.
          Read LIVE from the operator rather than from the snapshot beside it:
          the tax breakdown above is a record of what was charged and must never
          move, but these are the business's current identity. A reprint of a
          two-year-old invoice should carry the address post will reach today,
          not the office they have since left. */}
      <BusinessDetails operator={operator} />

      {breakdown.note === "no_tax_configured" ? (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--rms-muted-strong)" }}>
          {t("billing.noTaxWasCharged", "No tax was charged: this business has no tax rates configured. Set them up in Settings → Tax.")}
        </div>
      ) : null}
    </>
  );
}

function Row({ label, value, strong }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13.5 }}>
      <span style={{ color: "var(--rms-muted)" }}>{label}</span>
      <span style={{ fontWeight: strong ? 700 : 550 }}>{value}</span>
    </div>
  );
}

export default Billing;
